import React from "react";
import { personalInfo } from "../data/mock";
import { useLanguage } from "../i18n/LanguageContext";
import { Github, Linkedin, Mail, Heart, ArrowUp } from "lucide-react";
import Brand from "./Brand";
import { trackEvent } from "../lib/analytics";

const Footer = () => {
  const { t } = useLanguage();

  const navLinks = [
    { name: t("nav.about"), href: "#about" },
    { name: t("nav.experience"), href: "#experience" },
    { name: t("nav.projects"), href: "#projects" },
    { name: t("nav.techstack"), href: "#techstack" },
    { name: t("nav.certifications"), href: "#certifications" },
    { name: t("nav.contact"), href: "#contact" },
  ];

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  return (
    <footer className="relative bg-footer border-t border-surface-800/50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-12">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-10 mb-10">
          {/* Brand */}
          <div>
            <div className="mb-5"><Brand /></div>
            <p className="text-xs text-ink-600 leading-relaxed max-w-xs">
              {t("hero.tagline")}
            </p>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="font-mono text-xs text-ink-500 uppercase tracking-wider mb-4">
              {t("footer.navigation")}
            </h4>
            <ul className="space-y-2">
              {navLinks.map((link) => (
                <li key={link.href}>
                  <a
                    href={link.href}
                    className="text-xs text-ink-600 hover:text-gold-400 transition-colors duration-300 font-mono"
                    onClick={(e) => {
                      e.preventDefault();
                      trackEvent("nav_click", { section: link.href.replace("#", ""), location: "footer" });
                      const el = document.querySelector(link.href);
                      if (el) el.scrollIntoView({ behavior: "smooth" });
                    }}
                  >
                    {link.name}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          {/* Social + Contact */}
          <div>
            <h4 className="font-mono text-xs text-ink-500 uppercase tracking-wider mb-4">
              {t("footer.connect")}
            </h4>
            <div className="flex items-center gap-3 mb-4">
              <a
                href={personalInfo.github}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => trackEvent("social_click", { network: "github", location: "footer" })}
                className="w-9 h-9 rounded-lg border border-surface-800 flex items-center justify-center text-ink-600 hover:text-gold-400 hover:border-amber-500/30 transition-all duration-300"
              >
                <Github className="w-4 h-4" />
              </a>
              <a
                href={personalInfo.linkedin}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => trackEvent("social_click", { network: "linkedin", location: "footer" })}
                className="w-9 h-9 rounded-lg border border-surface-800 flex items-center justify-center text-ink-600 hover:text-gold-400 hover:border-amber-500/30 transition-all duration-300"
              >
                <Linkedin className="w-4 h-4" />
              </a>
              <a
                href={`mailto:${personalInfo.email}`}
                onClick={() => trackEvent("social_click", { network: "email", location: "footer" })}
                className="w-9 h-9 rounded-lg border border-surface-800 flex items-center justify-center text-ink-600 hover:text-gold-400 hover:border-amber-500/30 transition-all duration-300"
              >
                <Mail className="w-4 h-4" />
              </a>
            </div>
            <p className="text-xs text-ink-600 font-mono">
              {personalInfo.email}
            </p>
          </div>
        </div>

        {/* Bottom bar */}
        <div className="pt-8 border-t border-surface-800/50 flex flex-col sm:flex-row items-center justify-between gap-4">
          <p className="text-xs text-ink-700 font-mono flex items-center gap-1">
            {t("footer.madeWith")}{" "}
            <Heart className="w-3 h-3 text-gold-500/50" /> {t("footer.by")}{" "}
            {personalInfo.name}
          </p>
          <p className="text-xs text-ink-700 font-mono">
            &copy; {new Date().getFullYear()} {t("footer.rights")}
          </p>
          <button
            aria-label={t("identity.backTop")}
            onClick={scrollToTop}
            className="p-2 rounded-lg border border-surface-800 text-ink-600 hover:text-gold-400 hover:border-amber-500/30 transition-all duration-300"
          >
            <ArrowUp className="w-4 h-4" />
          </button>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
